import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { 
  BarChart3, Brain, Clock, Users, FileText, Bell, Palette, GitCompare, 
  Upload, Check, Star, ArrowRight, ChevronDown, Facebook, Target, 
  PieChart, Sparkles, Globe, Rocket, ArrowUpRight, TrendingUp, Zap, Shield, Chrome
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import SalesChat from '@/components/SalesChat';
import { changeLanguage } from '@/i18n';

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const currentLang = i18n.language;

  const platforms = [
    { name: 'Meta Ads', color: '#0668E1', icon: Facebook },
    { name: 'Google Ads', color: '#EA4335', icon: Target },
    { name: 'TikTok Ads', color: '#010101', icon: Sparkles },
    { name: 'Google Analytics 4', color: '#F9AB00', icon: PieChart },
    { name: 'Search Console', color: '#4285F4', icon: Globe }
  ];

  const features = [
    { icon: <BarChart3 className="w-6 h-6" />, titleKey: 'features.dashboard', color: '#2563EB', bg: '#EFF6FF' },
    { icon: <Brain className="w-6 h-6" />, titleKey: 'features.ai', color: '#7C3AED', bg: '#F5F3FF' },
    { icon: <GitCompare className="w-6 h-6" />, titleKey: 'features.comparison', color: '#059669', bg: '#ECFDF5' },
    { icon: <Bell className="w-6 h-6" />, titleKey: 'features.alerts', color: '#D97706', bg: '#FFFBEB' },
    { icon: <FileText className="w-6 h-6" />, titleKey: 'features.pdf', color: '#DC2626', bg: '#FEF2F2' },
    { icon: <Clock className="w-6 h-6" />, titleKey: 'features.reports', color: '#0891B2', bg: '#ECFEFF' },
    { icon: <Users className="w-6 h-6" />, titleKey: 'features.clients', color: '#DB2777', bg: '#FDF2F8' },
    { icon: <Palette className="w-6 h-6" />, titleKey: 'features.whitelabel', color: '#0D9488', bg: '#F0FDFA' }
  ];

  const steps = [
    { icon: Target, titleKey: 'howItWorks.step1', color: '#2563EB', bg: '#EFF6FF' },
    { icon: BarChart3, titleKey: 'howItWorks.step2', color: '#7C3AED', bg: '#F5F3FF' },
    { icon: Brain, titleKey: 'howItWorks.step3', color: '#059669', bg: '#ECFDF5' }
  ];

  const testimonials = [
    { key: 't1', avatar: currentLang === 'es' ? 'MG' : 'MJ', color: '#2563EB' },
    { key: 't2', avatar: currentLang === 'es' ? 'CR' : 'CS', color: '#7C3AED' },
    { key: 't3', avatar: currentLang === 'es' ? 'AM' : 'AW', color: '#059669' }
  ];

  const plans = [
    { key: 'starter', price: 19, priceAnnual: 190, variantId: '1261745', variantIdAnnual: '1272493', features: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6'], popular: false, trial: true },
    { key: 'pro', price: 49, priceAnnual: 490, variantId: '1272500', variantIdAnnual: '1272507', features: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8'], popular: true, trial: true },
    { key: 'agency', price: 149, priceAnnual: 1490, variantId: '1261751', variantIdAnnual: '1272498', features: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10'], popular: false, trial: true }
  ];

  const getAnnualSavings = (monthly, annual) => Math.round((monthly * 12) - annual);

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}>
      <SEO />

      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center shadow-sm">
              <Zap className="w-6 h-6 text-white" fill="white" strokeWidth={2.5} />
            </div>
            <span className="font-extrabold text-2xl text-gray-900 tracking-tight">AdPulse <span className="text-blue-600">IA</span></span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors">{t('nav.features')}</a>
            <a href="#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors">{t('nav.pricing')}</a>
            <a href="#faq" className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors">{t('nav.faq')}</a>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1 bg-gray-100 rounded-full p-1">
              <button onClick={() => changeLanguage('es')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${currentLang === 'es' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>ES</button>
              <button onClick={() => changeLanguage('en')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${currentLang === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>EN</button>
            </div>
            <button onClick={() => navigate('/login')} className="hidden sm:block text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors px-4 py-2">
              {t('nav.login')}
            </button>
            <button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm">
              {t('nav.startFree')}
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 via-white to-white pt-16 pb-0">
        {/* Background blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-40 -translate-y-1/2" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-violet-100 rounded-full blur-3xl opacity-30" />

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{t('hero.badge')}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 leading-tight tracking-tight mb-6">
              {t('hero.title1')}<br />
              <span className="text-blue-600">{t('hero.title2')}</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t('hero.subtitle').replace(/<strong>/g, '<strong class="text-gray-800 font-semibold">').replace(/<\/strong>/g, '</strong>') }}
            />

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button onClick={() => navigate('/register')} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-full text-base transition-all shadow-lg shadow-blue-200 hover:shadow-blue-300 flex items-center justify-center gap-2">
                <Rocket className="w-4 h-4" />{t('hero.cta')}
              </button>
              <button onClick={() => navigate('/login')} className="w-full sm:w-auto border border-gray-200 hover:border-gray-300 text-gray-700 font-medium px-8 py-4 rounded-full text-base transition-all hover:bg-gray-50 flex items-center justify-center gap-2">
                {t('hero.demo')} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>

            {/* Platform logos */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-12 text-sm text-gray-400">
              <span className="w-full sm:w-auto font-medium">{t('hero.compatible')}</span>
              {platforms.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-600">
                  <p.icon className="w-4 h-4" style={{ color: p.color }} />
                  <span className="font-medium text-xs">{p.name}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Dashboard mockup */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative mx-auto max-w-5xl">
            <div className="bg-white rounded-2xl shadow-2xl shadow-gray-200 border border-gray-100 overflow-hidden">
              {/* Browser bar */}
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-400 border border-gray-200 max-w-xs mx-auto text-center">app.adpulseai.com/dashboard</div>
              </div>
              {/* Dashboard content */}
              <div className="bg-gray-50 p-6">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Spend', value: '$12,840', trend: '+12%', color: '#2563EB', bg: '#EFF6FF' },
                    { label: 'Impressions', value: '2.4M', trend: '+8%', color: '#7C3AED', bg: '#F5F3FF' },
                    { label: 'CTR', value: '3.2%', trend: '+0.4%', color: '#059669', bg: '#ECFDF5' },
                    { label: 'Conversions', value: '1,248', trend: '+24%', color: '#D97706', bg: '#FFFBEB' }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold mt-1" style={{ color: stat.color }}>
                        <TrendingUp className="w-3 h-3" />{stat.trend}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 bg-white rounded-xl p-4 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Performance Overview</p>
                    <div className="flex items-end gap-1 h-24">
                      {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t-sm transition-all" style={{ height: `${h}%`, backgroundColor: i === 10 ? '#2563EB' : '#DBEAFE' }} />
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-3">By Platform</p>
                    <div className="space-y-2">
                      {[{ name: 'Meta', pct: 45, color: '#0668E1' }, { name: 'Google', pct: 35, color: '#EA4335' }, { name: 'TikTok', pct: 20, color: '#010101' }].map((p, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{p.name}</span><span>{p.pct}%</span></div>
                          <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-full rounded-full" style={{ width: `${p.pct}%`, backgroundColor: p.color }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Floating badges */}
            <div className="absolute -left-8 top-1/3 bg-white rounded-xl shadow-lg shadow-gray-200 border border-gray-100 p-3 hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-green-600" /></div>
              <div><p className="text-xs font-bold text-gray-900">+34% ROI</p><p className="text-xs text-gray-400">vs last month</p></div>
            </div>
            <div className="absolute -right-8 top-1/4 bg-white rounded-xl shadow-lg shadow-gray-200 border border-gray-100 p-3 hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Zap className="w-4 h-4 text-blue-600" /></div>
              <div><p className="text-xs font-bold text-gray-900">AI Insights</p><p className="text-xs text-gray-400">Ready</p></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SOCIAL PROOF LOGOS ── */}
      <section className="py-12 border-y border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-sm text-gray-400 font-medium mb-8">
            {currentLang === 'es' ? 'Compatible con las principales plataformas de marketing' : 'Works with the top marketing platforms'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16">
            {platforms.map((p, i) => (
              <div key={i} className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
                <p.icon className="w-5 h-5" style={{ color: p.color }} />
                <span className="text-sm font-semibold text-gray-700">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CHROME EXTENSION ── */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-4 block">Chrome Extension</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {currentLang === 'es' ? 'Tus métricas al alcance de un clic' : 'Your metrics one click away'}
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                {currentLang === 'es'
                  ? 'Accede a tus campañas, ranking, insights y reportes IA directamente desde tu navegador. Sin abrir el dashboard.'
                  : 'Access your campaigns, ranking, insights and AI reports directly from your browser. No need to open the dashboard.'}
              </p>
              <div className="space-y-4 mb-8">
                {[
                  currentLang === 'es' ? '⚡ Métricas en tiempo real por cliente' : '⚡ Real-time metrics per client',
                  currentLang === 'es' ? '🏆 Ranking de campañas con score' : '🏆 Campaign ranking with scoring',
                  currentLang === 'es' ? '🤖 Snap Report IA con un clic' : '🤖 AI Snap Report with one click',
                  currentLang === 'es' ? '📊 Meta Ads, Google Ads, GA4 y GSC' : '📊 Meta Ads, Google Ads, GA4 & GSC',
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-300">
                    <span className="text-lg">{f}</span>
                  </div>
                ))}
              </div>
              <a href="https://adpulseia.com/support" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all">
                <Chrome className="w-5 h-5" />
                {currentLang === 'es' ? 'Obtener Extensión' : 'Get Extension'}
              </a>
            </div>
            <div className="flex justify-center">
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 transform rotate-1 hover:rotate-0 transition-transform">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">⚡</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">AdPulse <span className="text-blue-600">IA</span></div>
                    <div className="text-xs text-gray-400">{currentLang === 'es' ? 'Métricas en tiempo real' : 'Real-time metrics'}</div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 mb-3">
                  <div className="text-xs text-gray-500 mb-1">GASTO</div>
                  <div className="text-2xl font-bold text-blue-600">$14,837K</div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">IMP</div><div className="font-bold text-sm">16.1M</div></div>
                  <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">CLICKS</div><div className="font-bold text-sm">366.6K</div></div>
                  <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">CTR</div><div className="font-bold text-sm text-purple-600">2.27%</div></div>
                  <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">CONV</div><div className="font-bold text-sm text-green-600">2</div></div>
                </div>
                <div className="border-t pt-3">
                  <div className="text-xs font-semibold text-gray-500 mb-2">🏆 RANKING</div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-yellow-400 text-white text-xs flex items-center justify-center font-bold">1</span><span className="font-medium text-gray-900">Trafico_Remarketing</span></div>
                    <span className="text-green-600 font-bold text-xs">$661K</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-gray-300 text-white text-xs flex items-center justify-center font-bold">2</span><span className="font-medium text-gray-900">Rev_Septiembre</span></div>
                    <span className="text-green-600 font-bold text-xs">$657K</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* ── STATS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-sm font-semibold text-blue-600 uppercase tracking-widest mb-3">
            {currentLang === 'es' ? 'AdPulse AI en números' : 'AdPulse AI by the numbers'}
          </p>
          <h2 className="text-center text-3xl sm:text-4xl font-extrabold text-gray-900 mb-16">
            {currentLang === 'es' ? 'Todo lo que necesitas para demostrar resultados' : 'Everything you need to prove results'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '5', label: t('stats.platforms'), sub: t('stats.platformsSub'), color: '#2563EB' },
              { value: '80%', label: t('stats.time'), sub: t('stats.timeSub'), color: '#7C3AED' },
              { value: '24/7', label: t('stats.monitoring'), sub: t('stats.monitoringSub'), color: '#059669' },
              { value: '∞', label: t('stats.campaigns'), sub: t('stats.campaignsSub'), color: '#D97706' }
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <div className="text-4xl sm:text-5xl font-extrabold mb-2" style={{ color: s.color }}>{s.value}</div>
                <div className="font-semibold text-gray-900 text-sm mb-1">{s.label}</div>
                <div className="text-xs text-gray-400">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES - dark section ── */}
      <section id="features" className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3">{currentLang === 'es' ? 'Funcionalidades' : 'Features'}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4">
              {t('features.title')} <span className="text-blue-400">{t('features.titleHighlight')}</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">{t('features.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 * i }}
                className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 hover:-translate-y-1 transition-all cursor-pointer group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform" style={{ backgroundColor: f.bg, color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-white text-base mb-2">{t(`${f.titleKey}.title`)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{t(`${f.titleKey}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">
              {currentLang === 'es' ? 'Cómo funciona' : 'How it works'}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
              {t('howItWorks.title')} <span className="text-blue-600">{t('howItWorks.titleHighlight')}</span> {t('howItWorks.titleEnd')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="text-center">
                <div className="relative mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg" style={{ backgroundColor: s.bg }}>
                  <s.icon className="w-7 h-7" style={{ color: s.color }} />
                  <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: s.color }}>{t('howItWorks.step')} {i + 1}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{t(`${s.titleKey}.title`)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(`${s.titleKey}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">
              {currentLang === 'es' ? 'Testimonios' : 'Testimonials'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              {t('testimonials.title')} <span className="text-blue-600">{t('testimonials.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {testimonials.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">"{t(`testimonials.${item.key}.text`)}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: item.color }}>
                    {item.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{t(`testimonials.${item.key}.name`)}</div>
                    <div className="text-gray-400 text-xs">{t(`testimonials.${item.key}.role`)}, {t(`testimonials.${item.key}.company`)}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">
              {currentLang === 'es' ? 'Precios' : 'Pricing'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              {t('pricing.title')} <span className="text-blue-600">{t('pricing.titleHighlight')}</span>
            </h2>
            <p className="text-gray-500 text-lg mb-8">{t('pricing.subtitle')}</p>

            {/* Toggle */}
            <div className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>
                {currentLang === 'es' ? 'Mensual' : 'Monthly'}
              </span>
              <button onClick={() => setIsAnnual(!isAnnual)} className={`relative w-12 h-6 rounded-full transition-colors ${isAnnual ? 'bg-blue-600' : 'bg-gray-200'}`}>
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isAnnual ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>
                  {currentLang === 'es' ? 'Anual' : 'Yearly'}
                </span>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">-20%</span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => {
              const currentPrice = isAnnual ? plan.priceAnnual : plan.price;
              const savings = getAnnualSavings(plan.price, plan.priceAnnual);
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className={`relative bg-white rounded-2xl p-8 border transition-all hover:-translate-y-1 hover:shadow-lg ${plan.popular ? 'border-blue-500 shadow-blue-100 shadow-lg ring-1 ring-blue-500' : 'border-gray-200'}`}>
                  {plan.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      {t('pricing.popular')}
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-900 text-lg mb-1">{t(`pricing.${plan.key}.name`)}</h3>
                    {plan.trial && (
                      <span className="inline-block bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                        {currentLang === 'es' ? '7 días gratis' : '7 days free trial'}
                      </span>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900">${currentPrice}</span>
                      <span className="text-gray-400 text-sm">/{isAnnual ? (currentLang === 'es' ? 'año' : 'year') : (currentLang === 'es' ? 'mes' : 'mo')}</span>
                    </div>
                    {isAnnual && savings > 0 && <p className="text-green-600 text-xs font-semibold mt-1">{currentLang === 'es' ? `Ahorras $${savings}` : `Save $${savings}`}</p>}
                    <p className="text-gray-500 text-sm mt-2">{t(`pricing.${plan.key}.desc`)}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-gray-700">
                        <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-blue-600" />
                        </div>
                        {t(`pricing.${plan.key}.${f}`)}
                      </li>
                    ))}
                  </ul>

                  <button onClick={() => navigate('/register')}
                    className={`w-full py-3 rounded-full font-semibold text-sm transition-all ${plan.popular ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}>
                    {plan.trial ? (currentLang === 'es' ? 'Probar 7 días gratis' : 'Start 7-day free trial') : t(`pricing.${plan.key}.cta`)}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              {t('faq.title')} <span className="text-blue-600">{t('faq.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {['1', '2', '3', '4', '5'].map((num, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{t(`faq.q${num}`)}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed">{t(`faq.a${num}`)}</div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-blue-500 text-blue-100 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              <Shield className="w-3.5 h-3.5" />
              {currentLang === 'es' ? '7 días gratis · Sin tarjeta de crédito' : '7 days free · No credit card required'}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">{t('cta.title')}</h2>
            <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">{t('cta.subtitle')}</p>
            <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 bg-white text-blue-600 font-bold px-10 py-4 rounded-full text-base hover:bg-blue-50 transition-colors shadow-xl shadow-blue-800/20">
              {t('cta.button')} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-blue-200 text-sm mt-4">{t('cta.note')}</p>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
              <BarChart3 className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-white">AdPulse <span className="text-blue-400">IA</span></span>
          </div>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <a href="#features" className="hover:text-white transition-colors">{t('nav.features')}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t('nav.pricing')}</a>
            <a href="#faq" className="hover:text-white transition-colors">{t('nav.faq')}</a>
          </div>
          <p className="text-gray-500 text-sm">© 2026 AdPulse AI. All rights reserved.</p>
          <div className="flex gap-6 mt-3 flex-wrap justify-center">
            <a href="/about" className="text-gray-500 hover:text-white text-sm transition-colors">About Us</a>
            <a href="/support" className="text-gray-500 hover:text-white text-sm transition-colors">Support</a>
            <a href="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">Terms of Service</a>
            <a href="mailto:soporte@adpulseia.com" className="text-gray-500 hover:text-white text-sm transition-colors">soporte@adpulseia.com</a>
            <a href="https://www.instagram.com/adpulseia/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-400 text-sm transition-colors flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </a>
          </div>
        </div>
      </footer>
      <SalesChat />
    </div>
  );
}